// David Walsh's debouncer
export function debounce(func, wait, immediate) {
  let timeout
  return function() {
    let context = this
    let args = arguments
    let later = function() {
      timeout = null
      if (!immediate) func.apply(context, args)
    }
    let callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func.apply(context, args)
  }
}

export function isHidden(element) {
  // Browsers hide all non-<summary> descendants of closed <details> elements
  // from user interaction, but those non-<summary> elements may still match our
  // focusable-selectors and may still have dimensions, so we need a special
  // case to ignore them.
  if (element.matches('details:not([open]) *') &&
    !element.matches('details>summary:first-of-type'))
    return true
  // If this element has no painted dimensions, it's hidden.
  return !(element.offsetWidth || element.offsetHeight || element.getClientRects().length)
}

export function isVisible(element) {
  return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length)
}

export function getFocusableEls(parentElement) {
  // From Kitty Giraudel's a11y-dialog
  // https://github.com/KittyGiraudel/a11y-dialog/tree/8.0.0

  const notInert = ':not([inert]):not([inert] *)'
  const notNegTabIndex = ':not([tabindex^="-"])'
  const notDisabled = ':not(:disabled)'

  const focusableSelectors = [
    `a[href]${notInert}${notNegTabIndex}`,
    `area[href]${notInert}${notNegTabIndex}`,
    `input:not([type="hidden"]):not([type="radio"])${notInert}${notNegTabIndex}${notDisabled}`,
    `input[type="radio"]${notInert}${notNegTabIndex}${notDisabled}`,
    `select${notInert}${notNegTabIndex}${notDisabled}`,
    `textarea${notInert}${notNegTabIndex}${notDisabled}`,
    `button${notInert}${notNegTabIndex}${notDisabled}`,
    `details${notInert} > summary:first-of-type${notNegTabIndex}`,
    `iframe${notInert}${notNegTabIndex}`,
    `audio[controls]${notInert}${notNegTabIndex}`,
    `video[controls]${notInert}${notNegTabIndex}`,
    `[contenteditable]${notInert}${notNegTabIndex}`,
    `[tabindex]${notInert}${notNegTabIndex}`,
  ]

  const focusableChildren = parentElement.querySelectorAll(focusableSelectors.join(','))
  return Array.prototype.slice.call(focusableChildren)
}

export function trapTabs(event, focusableEls) {
  let firstFocusableEl = focusableEls[0]
  let lastFocusableEl = focusableEls[focusableEls.length - 1]

  let handleBackwardTab = () => {
    if ( document.activeElement === firstFocusableEl ) {
      event.preventDefault()
      lastFocusableEl.focus()
    }
  }
  let handleForwardTab = () => {
    if ( document.activeElement === lastFocusableEl ) {
      event.preventDefault()
      firstFocusableEl.focus()
    }
  }
  if (event.keyCode === 9) {
    if ( focusableEls.length === 1 ) {
      event.preventDefault()
      return
    }
    event.shiftKey ? handleBackwardTab() : handleForwardTab()
  }
}

export function focusOnElement(el) {
  // Adapted from Anika Henke's solution:
  // https://github.com/selfthinker/dokuwiki_template_writr/blob/master/js/skip-link-focus-fix.js
  //
  // This still isn't working for VoiceOver in iOS.

  if (!el) return

  let focusables = getFocusableEls(document.body)
  let element = document.querySelector(el)

  if (!element) return

  if (focusables.indexOf(element) === -1) {
    // The element isn't focusable; we will temporarily make it so
    element.setAttribute('tabindex', -1)
    element.focus()

    // Don't keep the tabindex attribute any longer than necessary —
    // remove it as soon as focus leaves the element
    element.addEventListener('blur', function(e) {
      this.removeAttribute('tabindex')
    })
    element.addEventListener('focusout', function(e) {
      this.removeAttribute('tabindex')
    })
  }
}

export function getBreakpoints(element) {
  // Get breakpoints already declared in CSS, to avoid declaring the same values
  // in numerous different places. Mostly convenient with SCSS or LESS breakpoint
  // variables. To use, set up the element’s CSS as follows:
  //
  // - Add ::before or ::after to a relevant element of your choice.
  //
  // - Set these properties on the pseudo-element to hide it:
  //
  //   display: none;
  //   visibility: hidden;
  //
  // - Set the pseudo-element’s content property like the following, where the
  //   string begins with `bp:` and the breakpoint values are separated with a
  //   comma. Including whitespace is fine, it’ll get stripped out if it’s there.
  //   Each breakpoint value must include a unit of some kind (rem, em, px).
  //
  //   content: 'bp: [small value], [large value]';

  let beforeContent = getComputedStyle(element, '::before').content
  let afterContent = getComputedStyle(element, '::after').content

  let bpStringMatch = /bp:/
  let useBefore = beforeContent.match(bpStringMatch)
  let useAfter = afterContent.match(bpStringMatch)

  let bpString = useBefore ? beforeContent : afterContent
  let bpValues = bpString.replace(/"|\s|bp:/g, '').split(',')

  return {
    sm: bpValues[0],
    lg: bpValues[1]
  }
}

export function viewportSize(sm, lg) {
  const VIEWPORT_IS_SM = window.matchMedia(`(max-width: ${sm})`)
  const VIEWPORT_IS_LG = window.matchMedia(`(min-width: ${lg})`)

  return {
    isSm: VIEWPORT_IS_SM,
    isLg: VIEWPORT_IS_LG
  }
}

export function camelize(str) {
  return str.split(/[-_]/)
    .map(w => w.replace(/./, m => m.toUpperCase()))
    .join('').replace(/^\w/, c => c.toLowerCase())
}

export function getTransitionDuration(element) {
  let inSeconds = /\ds$/;
  let inMilliseconds = /\dms$/;
  let durationValue =
    window
      .getComputedStyle(element)
      .getPropertyValue('transition-duration')

  let ds = durationValue.replace(/[^0-9]$/, '')

  if (ds === '0') {
    console.warn('getTransitionDuration found a transition-duration value of 0 — check CSS declaration is set properly')
  }
  if (!durationValue.match(inMilliseconds) && !durationValue.match(inSeconds)) {
    console.warn('getTransitionDuration failed: make sure that CSS transition is declared in seconds (s) or milliseconds (ms)')
  }

  return durationValue.match(inMilliseconds) ? parseFloat(ds) : parseFloat(ds) * 1000
}


export function controlBackgroundVideo(video, buttonParent) {
  // The ability to pause a video that will play/loop for longer than 5 seconds must be present,
  // even for a muted autoplaying background video, to meet WCAG 2.2.2 Pause, Stop, Hide (Level A)
  // https://www.w3.org/WAI/WCAG21/Understanding/pause-stop-hide.html

  // Set up playback toggle button:
  const playbackBtn = document.createElement('button')
  playbackBtn.setAttribute('type', 'button')
  playbackBtn.classList.add('btn', 'video-bg-control')

  const playStr = gettext('Play background video')
  const pauseStr = gettext('Pause background video')

  const playIcon = `<svg viewBox="0 0 18 18" width="18" height="18" style="transform:translateX(0.1em)"><path d="M15.562 8.1L3.87.225C3.052-.337 2 .225 2 1.125v15.75c0 .9 1.052 1.462 1.87.9L15.563 9.9c.584-.45.584-1.35 0-1.8z"/></svg>`
  const pauseIcon = `<svg viewBox="0 0 18 18" width="18" height="18"><path d="M6 1H3c-.6 0-1 .4-1 1v14c0 .6.4 1 1 1h3c.6 0 1-.4 1-1V2c0-.6-.4-1-1-1zM12 1c-.6 0-1 .4-1 1v14c0 .6.4 1 1 1h3c.6 0 1-.4 1-1V2c0-.6-.4-1-1-1h-3z"/></svg>`

  const playContents = `${playIcon}<span class="u-vh">${playStr}</span>`
  const pauseContents = `${pauseIcon}<span class="u-vh">${pauseStr}</span>`

  // Check client-side autoplay settings:

  // Being able to check these autoplay policy properties instead of
  // the Promise-based check would have been neat. Only working on
  // Firefox as of January 2025. Ah well, someday, maybe.
  // const allMediaAllowed = navigator.getAutoplayPolicy('mediaelement') === 'allowed'
  // const mutedVideoAllowed = navigator.getAutoplayPolicy('mediaelement') === 'allowed-muted'

  const autoplayPromise = video.play()

  if (autoplayPromise !== undefined) {
    autoplayPromise.then(() => {
      console.log('autoplay successful, show pause button')
      playbackBtn.innerHTML = pauseContents
    }).catch(error => {
      console.log('autoplay prevented, show play button')
      playbackBtn.innerHTML = playContents
    })

    if (buttonParent.getAttribute('aria-hidden')) buttonParent.removeAttribute('aria-hidden')

    buttonParent.prepend(playbackBtn)

    // Handle playback toggling and button contents
    // for any further events:
    playbackBtn.addEventListener('click', event => {
      if (video.paused) {
        video.play()
      } else {
        video.pause()
      }
    })
    video.addEventListener('playing', event => {
      playbackBtn.innerHTML = pauseContents
    })
    video.addEventListener('pause', event => {
      playbackBtn.innerHTML = playContents
    })
  }

}
